import type { WindowState } from '@/wm/types';

/**
 * What the task switcher offers, and where the highlight goes.
 *
 * Kept apart from the overlay because all of it is arithmetic: which windows
 * are in the list, which one is lit when the box appears, and where the next
 * press moves. None of that needs a DOM to be wrong in.
 */

/** Icons per row before the box grows another one. XP's own wrap. */
export const COLUMNS = 7;

/**
 * The windows in the list, most recently used first.
 *
 * `zIndex` *is* the MRU order: raising a window is the only thing that
 * advances the counter, so the stack from the top down is the order they were
 * last used in. That is what makes one press of Alt+Tab a toggle between the
 * last two windows rather than a walk down a list.
 *
 * Owned dialogs are left out, which is both what Windows did — an owned window
 * never appeared in the switch list — and the same set that gets a taskbar
 * button here. A message box is on top of its owner anyway; there is nothing
 * to switch to.
 */
export function switchList(windows: readonly WindowState[]): WindowState[] {
  return windows.filter((w) => !w.dialog).sort((a, b) => b.zIndex - a.zIndex);
}

/**
 * Where the highlight starts.
 *
 * One press forward lands on the *second* window, not the first: the first is
 * the one you are already looking at, and Alt+Tab that did nothing would be a
 * strange key. Backwards starts at the end for the same reason.
 */
export function initialIndex(count: number, backwards: boolean): number {
  if (count <= 1) return 0;
  return backwards ? count - 1 : 1;
}

/** The next position, wrapping in both directions. */
export function step(index: number, count: number, backwards: boolean): number {
  if (count <= 0) return 0;
  return (index + (backwards ? -1 : 1) + count) % count;
}

/** How many rows of icons the box needs. */
export function rowCount(count: number): number {
  return Math.max(1, Math.ceil(count / COLUMNS));
}

/** How many icons are in the widest row, which is what sets the box's width. */
export function columnCount(count: number): number {
  return Math.max(1, Math.min(count, COLUMNS));
}
